import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useTheme } from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, ProfilePicture } from '@getro/rombo';
import { NetworkSchema } from '../../../schema/network';
import { hasAdvancedDesignSelector } from '../../../redux/selectors/network';

export const Step = ({
  network,
  current,
  handleNext,
  handlePrevious,
  steps,
  isNextActive,
  onChange,
  onboarding,
  saving,
  onBoardingComplete,
  error,
}) => {
  const Component = steps[current].component;
  const hasAdvancedDesign = hasAdvancedDesignSelector(network);
  const { colors = {} } = useTheme();

  return (
    <Box>
      <Box pb="120px" flexDirection="column">
        <Flex
          padding="16px 16px 0"
          flexDirection="column"
          sx={{
            gap: '16px',
          }}
        >
          <Flex sx={{ gap: '8px' }}>
            <Box width="48px" minWidth="48px">
              <ProfilePicture variant="square" name={network.name} imageUrl={network.logoUrl} />
            </Box>
            <Flex flexDirection="column" sx={{ gap: '4px' }}>
              <Text lineHeight="1.2" fontSize="18px" fontWeight="600" color="text.dark" as="h1">
                {network.name} Talent Network
              </Text>
              <Text lineHeight="1.4" fontSize="14px" fontWeight="400" color="text.main" as="p">
                Connect with companies in our network.
              </Text>
            </Flex>
          </Flex>
          <Flex sx={{ gap: '8px' }}>
            {steps.map((step, index) => (
              <Box
                key={step.id}
                sx={{
                  backgroundSize: '200% 100%',
                  backgroundImage: `linear-gradient(to right, ${colors.neutral?.['100']} 50%, ${
                    hasAdvancedDesign ? colors.navigationBar : colors.purple?.['400']
                  } 50%)`,
                  backgroundPosition: current >= index ? '-100% 0' : '0',
                  transition: 'background-position 1s',
                  height: '4px',
                  borderRadius: '8px',
                }}
                width={`calc(100vw / ${steps.length - 1})`}
              />
            ))}
          </Flex>
          {!onBoardingComplete && (
            <Box fontSize="12px" lineHeight="1.4" color="text.subtle">
              {steps[current].timer}
            </Box>
          )}

          <Box height="calc(100% - 72px)" width="100%" maxWidth="600px" margin="0px auto" flexGrow="1">
            {steps[current].description && (
              <Text color="text.main" fontSize="18px" lineHeight="1.4">
                {steps[current].description}
              </Text>
            )}
            {steps[current].title && (
              <Text as="h4" fontWeight="600" fontSize="20px" lineHeight="1.4" mb="40px" mt="56px" color="text.dark">
                {steps[current].title}
              </Text>
            )}
            <Box data-testid={steps[current].id}>
              <Component
                data={onboarding[steps[current].id]}
                network={network}
                onChange={onChange}
                variant={hasAdvancedDesign ? 'onboardingCustom' : 'onboarding'}
                {...steps[current]}
              />
            </Box>
          </Box>
        </Flex>
      </Box>
      {!onBoardingComplete && (
        <Flex
          sx={{
            borderTop: '1px solid',
            borderTopColor: '#DEE4ED',
            position: 'fixed',
            zIndex: 1,
            bottom: 0,
            left: 0,
            width: '100%',
          }}
          height="72px"
          minHeight="72px"
          alignItems="center"
          justifyContent="center"
          backgroundColor="white"
          paddingX="16px"
        >
          <Flex width="100%" maxWidth="600px" justifyContent={current === 0 ? 'flex-end' : 'space-between'}>
            {current > 0 && current < steps.length && (
              <Button
                width={['16px', 'initial']}
                padding={['0px', '8px 24px']}
                data-testid="back-button"
                onClick={handlePrevious}
                variant="tertiary"
                icon={ChevronLeft}
              >
                <Text display={['none', 'block']}>Back</Text>
              </Button>
            )}

            {current < steps.length && (
              <Flex sx={{ gap: '8px', alignItems: 'center' }}>
                {error && (
                  <Text as="p" fontSize="14px" color="text.error">
                    Something went wrong, try again or{' '}
                    <Text as="a" color="text.error" href="mailto:help@getro.com" target="_blank" rel="noreferrer">
                      contact us.
                    </Text>
                  </Text>
                )}
                <Button
                  data-testid="next-button"
                  sx={{
                    border: 'none',
                    bg: hasAdvancedDesign ? 'customPrimary' : 'primary',
                    pointerEvents: saving || !isNextActive ? 'none' : 'unset',
                    '&&:hover': { bg: hasAdvancedDesign ? 'customPrimary' : 'primary' },
                  }}
                  disabled={!isNextActive}
                  onClick={handleNext}
                  iconPosition="right"
                  icon={ChevronRight}
                  loading={saving}
                >
                  Next
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

Step.defaultProps = {
  current: 1,
  error: false,
};

Step.propTypes = {
  steps: PropTypes.array.isRequired,
  current: PropTypes.number,
  handleNext: PropTypes.func.isRequired,
  handlePrevious: PropTypes.func.isRequired,
  network: NetworkSchema.isRequired,
  isNextActive: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onboarding: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  onBoardingComplete: PropTypes.bool.isRequired,
  error: PropTypes.bool,
};

export default memo(Step);
